/**
 * The result of a sitting, as a student reads it.
 */

export interface ExamResult {
  attempt: {
    id: string;
    attemptNo: number;
    status: 'submitted' | 'expired';
    submitSource?: 'manual' | 'auto' | 'expired';
    startedAt: string;
    submittedAt: string;
    timeUsedSeconds: number;
    timeUsedMinutes: number;
  };
  paper?: {
    id: string;
    title: string;
    paperNumber: number;
    durationMinutes: number;
    passMark?: number;
  };
  totalQuestions: number;
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
  score: number;
  totalMarks: number;
  percentage: number;
  passed?: boolean;
}

/** Marks earned out of marks available, the way the requirement sheet shows it. */
export const scoreLine = (result: ExamResult): string => `${result.score} / ${result.totalMarks}`;

export const percentageLine = (result: ExamResult): string =>
  `${Math.round((result.percentage ?? 0) * 100) / 100}%`;

/** How long the paper took, in the words a student would use. */
export const formatTimeUsed = (seconds: number): string => {
  const safe = Math.max(0, Math.round((seconds || 0) / 60));

  if (safe < 60) {
    return `${safe} minute${safe === 1 ? '' : 's'}`;
  }

  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  const hourPart = `${hours} hour${hours === 1 ? '' : 's'}`;

  return rest === 0 ? hourPart : `${hourPart} ${rest} min`;
};

/** When the attempt was handed in. */
export const formatAttemptDate = (iso?: string): string => {
  if (!iso) {
    return '';
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Whether the paper was passed, when the paper carries a pass mark at all.
 * A paper with no pass mark reports nothing rather than implying failure.
 */
export const outcomeLabel = (result: ExamResult): string | undefined => {
  if (result?.passed === undefined || result?.passed === null) {
    return undefined;
  }
  return result.passed ? 'Passed' : 'Not passed';
};

/**
 * Why the paper closed. A student who ran out of time should be told, rather
 * than left wondering why their last answers are missing.
 */
export const closedBecause = (result: ExamResult): string | undefined => {
  if (result?.attempt?.status === 'expired' || result?.attempt?.submitSource === 'expired') {
    return 'The time ran out, so this paper was submitted and marked for you.';
  }
  if (result?.attempt?.submitSource === 'auto') {
    return 'The clock reached zero, so this paper was submitted for you.';
  }
  return undefined;
};
